/**
 * Document title mappings for different routes in the RBC app.
 * Maps URL paths to i18n translation keys.
 */

/**
 * Path to translation key mapping.
 * Translation keys reference the pageTitles section in rbc.translation.json
 */
export const PAGE_TITLE_KEYS: Record<string, string> = {
  // Stays (Hotels) - RBC uses /stays as the hotels path
  "/stays": "pageTitles.stays.landing",
  "/stays/search": "pageTitles.stays.search",
  "/stays/availability": "pageTitles.stays.availability",
  "/stays/shop": "pageTitles.stays.shop",
  "/stays/shop/room-selection": "pageTitles.stays.rooms",
  "/stays/book": "pageTitles.stays.book",
  "/stays/confirmation": "pageTitles.stays.confirmation", // Standalone confirmation page

  // Vacation Rentals (Homes) - uses "stays" terminology
  "/vacation-rentals": "pageTitles.stays.landing",
  "/vacation-rentals/search": "pageTitles.stays.search",
  "/vacation-rentals/availability": "pageTitles.stays.availability",
  "/vacation-rentals/shop": "pageTitles.stays.shop",
  "/vacation-rentals/shop/room-selection": "pageTitles.stays.rooms",
  "/vacation-rentals/book": "pageTitles.stays.book",
  "/vacation-rentals/confirmation": "pageTitles.stays.confirmation", // Standalone confirmation page

  // Cars
  "/cars": "pageTitles.cars.landing",
  "/cars/search": "pageTitles.cars.search",
  "/cars/availability": "pageTitles.cars.availability",
  "/cars/shop": "pageTitles.cars.shop",
  "/cars/book": "pageTitles.cars.book",
  "/cars/book/confirmation": "pageTitles.cars.confirmation",

  // Activities
  "/activities": "pageTitles.activities.landing",
  "/activities/search": "pageTitles.activities.search",
  "/activities/availability": "pageTitles.activities.availability",
  "/activities/shop": "pageTitles.activities.shop",
  "/activities/book": "pageTitles.activities.book",
  "/activities/book/confirmation": "pageTitles.activities.confirmation",

  // Flights
  "/flights": "pageTitles.flights.landing",
  "/flights/search": "pageTitles.flights.search",
  "/flights/shop/departure": "pageTitles.flights.departure",
  "/flights/shop/return": "pageTitles.flights.return",
  "/flights/shop/multicity": "pageTitles.flights.multicity",
  "/flights/shop/review": "pageTitles.flights.review",
  "/flights/book": "pageTitles.flights.book",
  "/flights/book/confirmation": "pageTitles.flights.confirmation",

  // Packages
  "/packages": "pageTitles.packages.landing",
  "/packages/search": "pageTitles.packages.search",
  "/packages/hotels/availability": "pageTitles.packages.hotelsAvailability",
  "/packages/hotels/shop": "pageTitles.packages.hotelsShop",
  "/packages/hotels/shop/room-selection": "pageTitles.packages.rooms",
  "/packages/flights/departure": "pageTitles.packages.flightsDeparture",
  "/packages/flights/return": "pageTitles.packages.flightsReturn",
  "/packages/review": "pageTitles.packages.review",
  "/packages/checkout": "pageTitles.packages.checkout",
  "/packages/checkout/confirmation": "pageTitles.packages.confirmation",

  // Default/Home
  "/": "pageTitles.home",
};

/**
 * Get the translation key for a given path.
 * Returns undefined if no matching path is found.
 *
 * @param pathname - The current URL pathname
 * @returns The i18n translation key for the page title, or undefined
 */
export function getTitleKeyForPath(pathname: string): string | undefined {
  // Try exact match first
  if (PAGE_TITLE_KEYS[pathname]) {
    return PAGE_TITLE_KEYS[pathname];
  }

  // Try to find the best matching parent path
  // For example, /hotels/shop/123 should match /hotels/shop
  const pathSegments = pathname.split("/").filter(Boolean);

  // Try progressively shorter paths
  for (let i = pathSegments.length; i > 0; i--) {
    const partialPath = "/" + pathSegments.slice(0, i).join("/");
    if (PAGE_TITLE_KEYS[partialPath]) {
      return PAGE_TITLE_KEYS[partialPath];
    }
  }

  return undefined;
}
